import type { NavigationGuard, RouteLocationRaw } from 'vue-router';
import type { ProjectRole } from '@cosmic-safari/shared';
import { useAuthStore } from '@/stores/auth.js';

const PUBLIC_PATHS = new Set([
  '/login',
  '/logout',
  '/forgot-password',
  '/reset-password',
  '/invitations/accept',
]);

// The project-role lattice, low → high. A route's `meta.requiredRole` names the
// minimum a caller must hold. Kept as a plain Record (not a TS enum) per
// CODE_STYLE §5; deliberately NOT exported — MembersTab (SD-036) duplicates the
// three comparison lines rather than drag the guard into a settings component.
const ROLE_ORDER: Record<ProjectRole, number> = {
  viewer: 1,
  reviewer: 2,
  admin: 3,
  owner: 4,
};

function hasRoleAtLeast(actual: ProjectRole | null, required: ProjectRole): boolean {
  if (actual === null) return false;
  return ROLE_ORDER[actual] >= ROLE_ORDER[required];
}

// Where an authenticated caller lands with no return-to. A `redirect` naming
// this path is redundant — login would send them there anyway — so producers
// build their /login location via loginLocationFor() and omit it.
export const DEFAULT_AUTHED_PATH = '/projects';

export function loginLocationFor(fullPath: string): RouteLocationRaw {
  if (fullPath === DEFAULT_AUTHED_PATH) return { path: '/login' };
  return { path: '/login', query: { redirect: fullPath } };
}

export function validateRedirectPath(s: string | undefined): string | null {
  if (typeof s !== 'string') return null;
  if (s.length === 0) return null;
  if (!s.startsWith('/')) return null;
  if (s.startsWith('//')) return null;
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f]/.test(s)) return null;
  return s;
}

export const authGuard: NavigationGuard = async (to) => {
  const auth = useAuthStore();

  if (auth.loading) {
    await auth.bootstrap();
  }

  // Public routes bypass the auth redirect. The exact-match Set covers the
  // static public paths; `meta.public` additionally covers dynamic public
  // routes (e.g. /set-password/:token) the Set cannot match (SD-041 ticket 15).
  if (PUBLIC_PATHS.has(to.path) || to.meta.public === true) {
    if (auth.isAuthenticated && to.path === '/login') {
      const redirect = validateRedirectPath(
        typeof to.query.redirect === 'string' ? to.query.redirect : undefined
      );
      return redirect ?? { path: DEFAULT_AUTHED_PATH };
    }
    return true;
  }

  if (!auth.isAuthenticated) {
    return loginLocationFor(to.fullPath);
  }

  // Staff-only routes: a non-staff (but authenticated) caller is rewritten to
  // the named not-found catch-all with the URL preserved — byte-identical to
  // visiting a route that doesn't exist, so the admin area leaves no trace.
  if (to.meta.staff && !auth.isStaff) {
    return {
      name: 'not-found',
      params: { pathMatch: to.path.substring(1).split('/') },
      query: to.query,
      hash: to.hash,
    };
  }

  // Role-restricted routes: an authenticated-but-underprivileged caller is sent
  // to the per-project 403 page — NOT /login (the user IS logged in; login is
  // the wrong remediation, SD-036 §Critical findings #4). This runs only after
  // bootstrap() has settled above, so roleFor() reads a hydrated membership list
  // and a hard reload does not flash the 403 while `me` is in flight.
  const requiredRole = to.meta.requiredRole;
  if (requiredRole) {
    const projectId = to.params.projectId;
    const actual = typeof projectId === 'string' ? auth.roleFor(projectId) : null;
    if (!hasRoleAtLeast(actual, requiredRole)) {
      return { path: `/projects/${String(projectId)}/forbidden` };
    }
  }

  return true;
};
